"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = require("react");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _components = require("@folio/stripes/components");
var _core = require("@folio/stripes/core");
var _hooks = require("../hooks");
var _SearchField = _interopRequireDefault(require("../SearchField"));
var _ActionList = _interopRequireDefault(require("../ActionList"));
var _validators = require("../validators");
var _utils = require("../utils");
var _CustomProperties = _interopRequireDefault(require("../../../styles/CustomProperties.css"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
const propTypes = {
  afterQueryCalls: _propTypes.default.object,
  catchQueryCalls: _propTypes.default.object,
  displayConditions: _propTypes.default.shape({
    create: _propTypes.default.bool,
    delete: _propTypes.default.bool,
    view: _propTypes.default.bool
  }),
  handleRefdataCategoryClick: _propTypes.default.func,
  hideCreateButton: _propTypes.default.bool,
  intlKey: _propTypes.default.string,
  intlNS: _propTypes.default.string,
  isSearchDisabled: _propTypes.default.bool,
  label: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.node]),
  labelOverrides: _propTypes.default.object,
  onConfirmDelete: _propTypes.default.func,
  refdataEndpoint: _propTypes.default.string
};
const EditableRefdataCategoryList = /*#__PURE__*/(0, _react.forwardRef)((_ref, ref) => {
  var _deleteModal$refdata2;
  let {
    afterQueryCalls,
    catchQueryCalls,
    /*
     * Set of extra booleans for controlling access to actions
     * create/delete (View should be handled externally)
     * This will not overwrite "internal" behaviour, ie setting
     * delete to 'true' here would still not render a delete button
     * for a refdata category that has refdata values.
     */
    displayConditions = {
      create: true,
      delete: true
    },
    handleRefdataCategoryClick,
    hideCreateButton,
    isSearchDisabled,
    intlKey: passedIntlKey,
    intlNS: passedIntlNS,
    label,
    labelOverrides = {},
    // An object containing translation alternatives
    // A function which will fire on confirmation of delete,
    // with id of deleted Refdata category
    onConfirmDelete = _id => null,
    refdataEndpoint
  } = _ref;
  /* A component that allows for editing of refdata categories */
  const callout = (0, _react.useContext)(_core.CalloutContext);
  const kintIntl = (0, _hooks.useKintIntl)(passedIntlKey, passedIntlNS);
  const {
    create: createCondition = true,
    delete: deleteCondition = true
  } = displayConditions;

  // fetch refdata values
  const {
    data: refdata = [],
    isLoading: isRefdataLoading
  } = (0, _hooks.useRefdata)({
    endpoint: refdataEndpoint,
    returnQueryObject: true
  });
  const [searchTerm, setSearchTerm] = (0, _react.useState)('');
  const [contentData, setContentData] = (0, _react.useState)([]);
  const [deleteModal, setDeleteModal] = (0, _react.useState)({
    visible: false,
    refdata: null
  });
  const sortByDesc = (a, b) => a.desc.localeCompare(b.desc);
  (0, _react.useEffect)(() => {
    if (!isRefdataLoading) {
      if (searchTerm) {
        var _filteredRefdata$sort;
        const filteredRefdata = refdata === null || refdata === void 0 ? void 0 : refdata.filter(rd => rd.desc.toLowerCase().includes(searchTerm.toLowerCase()));
        setContentData((_filteredRefdata$sort = filteredRefdata === null || filteredRefdata === void 0 ? void 0 : filteredRefdata.sort(sortByDesc)) !== null && _filteredRefdata$sort !== void 0 ? _filteredRefdata$sort : []);
      } else {
        var _refdata$sort;
        setContentData((_refdata$sort = refdata === null || refdata === void 0 ? void 0 : refdata.sort(sortByDesc)) !== null && _refdata$sort !== void 0 ? _refdata$sort : []);
      }
    }
  }, [isRefdataLoading, refdata, searchTerm]);

  // Edit and Create will use the same POST mutation
  const {
    delete: deleteRefdataCategory,
    post: createRefdataCategory
  } = (0, _hooks.useMutateRefdataCategory)({
    afterQueryCalls: {
      delete: json => {
        if (afterQueryCalls !== null && afterQueryCalls !== void 0 && afterQueryCalls.delete) {
          afterQueryCalls.delete(json);
        }
      },
      post: json => {
        if (afterQueryCalls !== null && afterQueryCalls !== void 0 && afterQueryCalls.post) {
          afterQueryCalls.post(json);
        }
      }
    },
    catchQueryCalls: {
      // Default delete behaviour is to fire a callout, either with kint-components default message
      // or one provided in labelOverrides, which is passed the error message and refdata in question
      delete: async err => {
        var _deleteModal$refdata;
        const errorResp = await (0, _utils.parseErrorResponse)(err.response);
        callout.sendCallout({
          message: kintIntl.formatKintMessage({
            id: 'refdataCategory.deleteRefdataCategory.errorMessage',
            overrideValue: labelOverrides === null || labelOverrides === void 0 ? void 0 : labelOverrides.deleteError
          }, {
            label: deleteModal === null || deleteModal === void 0 || (_deleteModal$refdata = deleteModal.refdata) === null || _deleteModal$refdata === void 0 ? void 0 : _deleteModal$refdata.label,
            error: errorResp === null || errorResp === void 0 ? void 0 : errorResp.message
          }),
          type: 'error'
        });
      },
      ...catchQueryCalls // override defaults here
    },
    endpoint: refdataEndpoint,
    id: refdata === null || refdata === void 0 ? void 0 : refdata.id,
    queryParams: {
      delete: {
        enabled: !!refdata
      },
      post: {
        enabled: !!refdata
      }
    }
  });
  if (isRefdataLoading) {
    return 'loading';
  }

  // This is the function which will take a row in the table and assign the relevant actions to it
  const actionAssigner = rowData => {
    var _rowData$values;
    const actionArray = [];
    if (!(rowData !== null && rowData !== void 0 && (_rowData$values = rowData.values) !== null && _rowData$values !== void 0 && _rowData$values.length) && deleteCondition) {
      actionArray.push({
        name: 'delete',
        label: kintIntl.formatKintMessage({
          id: 'delete',
          overrideValue: labelOverrides === null || labelOverrides === void 0 ? void 0 : labelOverrides.delete
        }),
        icon: 'trash',
        callback: data => setDeleteModal({
          visible: true,
          refdata: data
        }),
        ariaLabel: data => kintIntl.formatKintMessage({
          id: 'refdataCategory.deleteAriaLabel',
          overrideValue: labelOverrides === null || labelOverrides === void 0 ? void 0 : labelOverrides.deleteAriaLabel
        }, {
          label: data === null || data === void 0 ? void 0 : data.label
        })
      });
    }
    return actionArray;
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
    children: [!isSearchDisabled ? /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      className: _CustomProperties.default.lookupSearchContainer,
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_SearchField.default, {
        ariaLabel: kintIntl.formatKintMessage({
          id: 'refdataCategories.config.searchAriaLabel',
          overrideValue: labelOverrides.searchAriaLabel,
          fallbackMessage: 'refdata-category-search-field'
        }),
        className: _CustomProperties.default.lookupSearch,
        marginBottom0: true,
        onChange: e => setSearchTerm(e.target.value),
        value: searchTerm
      })
    }) : null, /*#__PURE__*/(0, _jsxRuntime.jsx)(_ActionList.default, {
      ref: ref,
      actionAssigner: actionAssigner,
      columnMapping: {
        desc: kintIntl.formatKintMessage({
          id: 'refdataCategory.refdataCategory',
          overrideValue: labelOverrides === null || labelOverrides === void 0 ? void 0 : labelOverrides.refdataCategory
        }),
        values: kintIntl.formatKintMessage({
          id: 'refdataCategory.noOfValues',
          overrideValue: labelOverrides === null || labelOverrides === void 0 ? void 0 : labelOverrides.noOfValues
        })
      },
      contentData: contentData,
      creatableFields: {
        values: () => false
      },
      createCallback: !createCondition ? null : data => createRefdataCategory(data),
      formatter: {
        desc: rowData => {
          if (handleRefdataCategoryClick) {
            return /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Button, {
              buttonStyle: "link",
              onClick: () => handleRefdataCategoryClick(rowData),
              children: rowData === null || rowData === void 0 ? void 0 : rowData.desc
            });
          }
          return rowData === null || rowData === void 0 ? void 0 : rowData.desc;
        },
        values: rowData => {
          var _rowData$values2;
          return rowData === null || rowData === void 0 || (_rowData$values2 = rowData.values) === null || _rowData$values2 === void 0 ? void 0 : _rowData$values2.length;
        }
      }
      /* Hide actions column when no permissions, or no deletable refdata categories */,
      hideActionsColumn: !createCondition && !deleteCondition || !(contentData !== null && contentData !== void 0 && contentData.find(cd => {
        var _cd$values;
        return (cd === null || cd === void 0 || (_cd$values = cd.values) === null || _cd$values === void 0 ? void 0 : _cd$values.length) === 0;
      })),
      hideCreateButton: hideCreateButton,
      label: label,
      validateFields: {
        desc: () => _validators.required
      },
      visibleFields: ['desc', 'values']
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.ConfirmationModal, {
      confirmLabel: kintIntl.formatKintMessage({
        id: 'delete',
        overrideValue: labelOverrides === null || labelOverrides === void 0 ? void 0 : labelOverrides.delete
      }),
      heading: kintIntl.formatKintMessage({
        id: 'refdataCategory.deleteRefdataCategory',
        overrideValue: labelOverrides === null || labelOverrides === void 0 ? void 0 : labelOverrides.deleteRefdataCategory
      }),
      message: kintIntl.formatKintMessage({
        id: 'refdataCategory.deleteRefdataCategory.confirmMessage',
        overrideValue: labelOverrides === null || labelOverrides === void 0 ? void 0 : labelOverrides.deleteRefdataCategoryMessage
      }, {
        name: deleteModal === null || deleteModal === void 0 || (_deleteModal$refdata2 = deleteModal.refdata) === null || _deleteModal$refdata2 === void 0 ? void 0 : _deleteModal$refdata2.desc
      }),
      onCancel: () => setDeleteModal({
        visible: false,
        refdata: null
      }),
      onConfirm: () => {
        var _deleteModal$refdata3, _deleteModal$refdata4;
        deleteRefdataCategory(deleteModal === null || deleteModal === void 0 || (_deleteModal$refdata3 = deleteModal.refdata) === null || _deleteModal$refdata3 === void 0 ? void 0 : _deleteModal$refdata3.id);
        onConfirmDelete(deleteModal === null || deleteModal === void 0 || (_deleteModal$refdata4 = deleteModal.refdata) === null || _deleteModal$refdata4 === void 0 ? void 0 : _deleteModal$refdata4.id);
        setDeleteModal({
          visible: false,
          refdata: null
        });
      },
      open: deleteModal === null || deleteModal === void 0 ? void 0 : deleteModal.visible
    })]
  });
});
EditableRefdataCategoryList.propTypes = propTypes;
var _default = exports.default = EditableRefdataCategoryList;